import React from "react";
import ProfileDetailsForm from "./profileDetailsForm/ProfileDetailsForm";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {resolveTab} from "src/utils/tab.util";
import {Alert, Box, Divider, Stack, Tab} from "@mui/material";
import {TabContext} from "@mui/lab";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import FolioList from "../billing/folio/FolioList";
import {MixpanelService} from "crqlar-react-common/dist/services/analytics/mixpanel.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import ProfileStayReservationList from "./ProfileStayReservationList";
import Typography from "@mui/material/Typography";
import FormSubmissions from "../../marketing/forms/FormSubmissions";
import ProfileSmartData from "./ProfileSmartData";
import ProfileAgenda from "./ProfileAgenda";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {useTranslation} from "react-i18next";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import ScrollableTabList from "crqlar-react-common/dist/components/tabs/ScrollableTabList";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import SurveyInviteButton from "../../surveys/share/SurveyInviteButton";

const ProfileDetails = () => {

    useTrackScreenVisit('system profile details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const readOnly = useReadOnly()

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = resolveTab(searchParams.get('tab'), ['basic', 'stays', 'folios', 'forms', 'smartData', 'agenda'], 'basic');
    const followMergeParam = searchParams.get('followMerge');
    const setActiveTab = (newTab: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('tab', newTab);
        setSearchParams(nextSearchParams);
    };

    const profileData = useEntity<Profile>({
        get: async () => {
            if (id && id !== 'new') {
                return ProfileService.getProfileById(id, followMergeParam !== null ? followMergeParam === 'true' : undefined)
            } else {
                return {}
            }
        },
        create: ProfileService.createProfile,
        update: ProfileService.updateProfile,
        delete: (id) => ProfileService.deleteProfile({id: [id]}),
        onDeleted: () => {
            navigate('/system/profiles', {replace: true})
        },
        onSaved: (profile, wasCreateAction, prev) => {
            if (wasCreateAction) {
                MixpanelService.getInstance().track('create profile')
                navigate(`/system/profiles/${profile.id}`, {replace: true})
            }
        },
    }, id)

    const profile = profileData.item
    const isMissingProfile = profileData.isMissing

    const headerSection = (
        <SettingsEntityHeader
            title={formatProfileListName(profile)}
            data={profileData}
            back={() => navigate('/system/profiles')}
        />
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete profile')}
                description={t('This permanently deletes the profile. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={profileData.saving}
                disabled={readOnly}
                onDelete={() => profileData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {profile.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    const content = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                gap: 6,
            }}
        >
                {
                    isMissingProfile ? (
                       <Box sx={{mb: 3}}>
                           <Alert severity={'info'} variant={'outlined'}>
                               {t('This profile no longer exists. It may have been deleted or recreated.')}
                           </Alert>
                       </Box>
                    ) : (
                        <>
                            {
                                profile.id &&
                                <TabContext value={activeTab}>
                                    <ScrollableTabList onChange={(e, v) => setActiveTab(v)}>
                                        <Tab label={t('Basic info')} value={'basic'}/>
                                        <Tab label={t('Smart Data')} value={'smartData'}/>
                                        <Tab label={t('Folios')} value={'folios'}/>
                                        <Tab label={t('Stays')} value={'stays'}/>
                                        <Tab label={t('Agenda')} value={'agenda'}/>
                                        <Tab label={t('Forms & Surveys')} value={'forms'}/>
                                    </ScrollableTabList>
                                </TabContext>
                            }
                        </>
                    )
                }

                {
                    !isMissingProfile && activeTab === 'basic' &&
                    <>
                        <ProfileDetailsForm
                            profileData={profileData}
                        />

                        {
                            profile.id &&
                            <>
                                <Divider sx={{mt: 4}}/>
                                {dangerZoneSection}
                            </>
                        }
                    </>
                }

                {
                    !isMissingProfile && activeTab === 'smartData' && profile.id &&
                    <ProfileSmartData profile={profile} onUnmerged={() => profileData.fetch()}/>
                }

                {
                    !isMissingProfile && activeTab === 'folios' &&
                    <div>
                        <FolioList profileId={id}/>
                    </div>
                }

                {
                    !isMissingProfile && activeTab === 'stays' && id &&
                    <div>
                        <ProfileStayReservationList profileId={id}/>
                    </div>
                }

                {
                    !isMissingProfile && activeTab === 'agenda' && id &&
                    <div>
                        <ProfileAgenda profileId={id}/>
                    </div>
                }

                {
                    !isMissingProfile && activeTab === 'forms' && id &&
                    <Stack gap={2}>
                        <Stack direction={'row'} justifyContent={'flex-end'}>
                            <SurveyInviteButton profileId={profile.id}/>
                        </Stack>
                        <FormSubmissions profileId={id}/>
                    </Stack>
                }
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {!profileData.loading && content}
        </Box>
    )

}

export default ProfileDetails;
